import PropTypes from 'prop-types';
import React from 'react';
import Donut from 'components/Donut';
import Busy from 'components/activity/Busy';
import {
  selectIsExternal,
  selectIsPublic,
  selectIsEmbed,
} from 'state/environment/environmentSelectors';
import {details as ErrorDetails} from 'components/VisualizationStrategies/Error/ErrorStrategy';
import LongPressGesture from 'components/gestures/LongPressGesture';
import isEqual from 'lodash/isEqual';
import get from 'lodash/get';
import {fetchChart} from 'state/card/cardService';
import SummaryNumber from 'components/VisualizationStrategies/SummaryNumber';
import {COLOR__DOMOBLUE_A_40} from 'constants/Colors';
import PdpNotice from 'components/PdpNotice';
import {ADOBE_ANALYTICS_EVENTS} from 'app/Analytics';
import {
  analyticsTrack,
  analyticsTrackWithFrameDuration,
} from 'utilities/analytics';
import {LazilyLoadFactory} from 'components/LazilyLoad';
import {between, easeOutQuad} from 'utilities/animation/AnimationMath';
import {updateChartState} from 'state/chart/chartActions';
import {selectChartStateByChartURN} from 'state/chart/chartSelectors';
import {connect} from 'react-redux';
import {getScaledDimensions} from 'utilities/geometry';
import {CARD_SETTINGS} from 'app/CardConstants';
import {selectCardById} from 'state/card/cardSelectors';
import {selectIsMap} from 'state/cardDetail/cardDetailSelectors';
import {selectIsCardSettingEnabled} from 'state/cardSetting/cardSettingSelectors';
import cx from 'classnames';
import './PhoenixChart.css';
import {selectIsFeatureEnabled} from 'state/feature/featureSelectors';
import {FEATURE_SWITCHES} from 'components/FeatureSwitch';

const importPhoenix = () => import(/* webpackChunkName: 'phoenix' */ 'phoenix');

const SUMMARY_NUMBER_HEIGHT = 48;
const FOCUS_LINE_SIZE = 8;
const FOCUS_SIZE = 120;
const FOCUS_ANIMATION_DEFER = 0.15;
const FOCUS_OPACITY_ANIMATION_THRESHOLD = 0.5;

const CLOSE_CHART_PICKER_REQUEST = 'close graph picker';

const getChartJSON = state =>
  state.isBridge ? state.response.bridge : state.response.chart;

class PhoenixChart extends React.Component {
  constructor() {
    super(...arguments);
    this.state = {
      isLoading: true,
      isConsideringFocus: false,
      isBridge: false,
      focusingX: 0,
      focusingY: 0,
      focusingProgress: 0,
      hasFocusSupport: false,
      hasError: false,
      x: 0,
      y: 0,
      scale: 1,
    };
    this.handleDrill = this.handleDrill.bind(this);
    this.handleLongPressStart = this.handleLongPressStart.bind(this);
    this.handleLongPressProgress = this.handleLongPressProgress.bind(this);
    this.handleLongPressCancel = this.handleLongPressCancel.bind(this);
    this.handleLongPressEnd = this.handleLongPressEnd.bind(this);
    this.handleMetricLogger = this.handleMetricLogger.bind(this);
    this.handleStateChange = this.handleStateChange.bind(this);
    this.handleDrillLinkEvent = this.handleDrillLinkEvent.bind(this);
  }

  componentDidMount() {
    this.fetch(this.props, {
      onlyFetchData: false,
      trigger: 'initial_load',
    });
  }

  componentDidUpdate(previousProps, previousState) {
    const didCardChange = this.props.urn !== previousProps.urn;
    const didDataChange =
      this.props.dateTimeElement !== previousProps.dateTimeElement ||
      !isEqual(this.props.dateRangeFilter, previousProps.dateRangeFilter) ||
      !isEqual(this.props.filters, previousProps.filters) ||
      !isEqual(this.props.functionOverrides, previousProps.functionOverrides) ||
      !isEqual(this.props.filterGroupIds, previousProps.filterGroupIds);

    if (didCardChange || didDataChange) {
      this.fetch(this.props, {
        onlyFetchData: didDataChange && !didCardChange && !this.props.isMap,
      });
    }

    if (this.state.isLoading) return null;
    if (!this.chart || previousState.isBridge !== this.state.isBridge) {
      this.renderChart();
    } else {
      this.updateChart(previousProps, previousState);
    }
  }

  renderChart() {
    this.track();
    this.destroyChart();
    const {height, width} = this.getInitialSize(this.props);
    this.chart = this.props.phoenix.createPhoenixWithChartState(
      getChartJSON(this.state),
      JSON.stringify(this.props.chartState),
      width,
      height,
      this.props.isPublic || this.props.isEmbed,
      1
    );
    this.chart.setUsePhoenixHover(true);

    if (this.isLockedRatio()) {
      // scale the map to the correct size
      const size = this.getChartSize();
      this.chart.scaleCanvas(size.width, size.height);
    }

    if (this.props.isDrillEnabled) {
      this.chart.addEventListener('drill', this.handleDrill);
    }
    this.chart.addEventListener('metricLogger', this.handleMetricLogger);
    this.chart.addEventListener('chartStateChanged', this.handleStateChange);
    this.chart.addEventListener(
      'displayAnnotation',
      this.handleDisplayAnnotation
    );
    this.container.appendChild(this.chart.getCanvas());
    this.chart.draw();
    this.renderPicker();

    this.setState({
      hasFocusSupport: this.chart.supportsDrag(),
    });
  }

  updateChart(previousProps, previousState) {
    if (getChartJSON(previousState) !== getChartJSON(this.state)) {
      this.track();
      this.chart.updateChartJson(getChartJSON(this.state));
    }

    if (
      previousProps.width !== this.props.width ||
      previousProps.chartHeight !== this.props.chartHeight
    ) {
      const size = this.getChartSize();
      if (!this.props.isMap) {
        this.chart.resize(size.width, size.height);
      } else {
        this.chart.scaleCanvas(size.width, size.height);
      }
    }

    if (previousProps.isPicker !== this.props.isPicker) {
      this.renderPicker();
    }
  }

  destroyChart() {
    if (!this.chart) return;

    this.container.removeChild(this.chart.getCanvas());
    this.chart.destroy();
  }

  renderPicker() {
    if (this.props.isPicker) {
      this.chart.showChartPicker();
    } else {
      this.chart.hideChartPicker();
    }
  }

  track() {
    analyticsTrackWithFrameDuration(ADOBE_ANALYTICS_EVENTS.CARD__RENDER, {
      cardURN: this.props.urn,
    });
  }

  handleMetricLogger(metric, properties) {
    analyticsTrack(metric, properties);
  }

  handleLongPressStart(focusingX, focusingY) {
    this.setState({
      focusingX,
      focusingY,
      focusingProgress: 0,
      isConsideringFocus: true,
    });
  }

  handleLongPressProgress(focusingX, focusingY, focusingProgress) {
    this.setState({
      focusingProgress,
      isConsideringFocus: true,
    });
  }

  handleLongPressCancel() {
    this.setState({
      isConsideringFocus: false,
    });
  }

  handleLongPressEnd() {
    this.setState({
      isConsideringFocus: false,
    });
  }

  handleDrill(drill) {
    const {filters} = drill.drillInfo;
    if (drill.drillInfo.internal === CLOSE_CHART_PICKER_REQUEST) {
      this.setState({
        hasFocusSupport: this.chart.supportsDrag(),
      });
      return this.props.onRequestChartPickerClose();
    }

    if (drill.drillInfo && drill.drillInfo.link) {
      return this.handleDrillLinkEvent(drill.drillInfo.link);
    }

    if (drill.drillInfo.internal == null && filters) {
      this.props.onDrill(this.props.urn, filters);
    }
  }

  handleDrillLinkEvent(linkObject) {
    const {target = '_self', url} = linkObject;
    window.open(url, target, 'noopener,noreferrer');
    // The return value here is used by phoenix to know if this action should trigger a repaint or not.
    return false;
  }

  fetch(props, options) {
    this.setState({
      isLoading: true,
    });

    const {width, height} = this.getInitialSize(this.props);

    fetchChart(props.urn, {
      width,
      height,
      filters: props.filters,
      cardLoadContext: {trigger: options.trigger},
      functionOverrides: props.functionOverrides,
      dataControlContext: {filterGroupIds: props.filterGroupIds},
      dateTimeElement: props.dateTimeElement,
      dateRangeFilter: props.dateRangeFilter,
      mobile: true,
      parts: [
        options.onlyFetchData ? 'data' : 'graph',
        'summary',
        'annotations',
        props.isExternal ? null : 'pdp',
      ].filter(Boolean),
      isAddFilterTypeEnabled: props.isAddFilterTypeEnabled,
    }).then(
      response => {
        this.setState(
          {
            isBridge: !!response.bridge,
            isLoading: false,
            hasError: false,
            response,
          },
          () => {
            this.props.onLoad({
              isChartPickerAvailable: !!(
                this.chart && this.chart.isChartPickerAvailable()
              ),
            });
          }
        );
      },
      () => {
        this.setState({
          hasError: true,
        });
      }
    );
  }

  handleStateChange(state) {
    this.props.updateChartState(this.props.urn, state);
  }

  handleDisplayAnnotation = e => {
    this.props.onDisplayAnnotation(JSON.parse(e));
  };

  isLockedRatio() {
    // the ratio must be locked when resizing charts from genus-advanced (aka maps)
    return this.props.isMap && this.props.isPublic;
  }

  getInitialSize({width, chartHeight}) {
    if (this.isLockedRatio()) {
      const maxSize = {
        width: 960,
        height: 570,
      };
      const minSize = {
        width: 480,
        height: 285,
      };

      width = Math.min(Math.max(width, minSize.width), maxSize.width);
      chartHeight = Math.min(
        Math.max(chartHeight, minSize.height),
        maxSize.height
      );
      return getScaledDimensions(
        {
          width,
          height: chartHeight,
        },
        maxSize
      );
    }

    return this.getChartSize({width, chartHeight});
  }

  getChartSize(props = this.props) {
    if (this.isLockedRatio()) {
      const packet = JSON.parse(getChartJSON(this.state));
      const bounds = get(packet, ['csrJson', 'Chart', 'bounds']);

      if (bounds && bounds.height && bounds.width) {
        return getScaledDimensions(
          {
            width: props.width,
            height: props.chartHeight,
          },
          bounds
        );
      }
    }

    return {
      width: props.width,
      height: props.chartHeight,
    };
  }

  render() {
    const {isLoading, response} = this.state;
    const {isSummaryEnabled} = this.props;
    if (this.state.hasError) return <ErrorDetails />;
    const showSummaryNumber = isSummaryEnabled && response && response.summary;

    return (
      <Busy isLoading={isLoading}>
        <div
          className={cx('PhoenixChart', isLoading && 'PhoenixChart--loading')}
          style={{minHeight: this.props.height}}
        >
          {showSummaryNumber ? (
            <SummaryNumber
              number={response && response.summary.value}
              label={response && response.summary.label}
            />
          ) : null}
          <LongPressGesture
            when={
              this.state.hasFocusSupport &&
              this.state.scale === 1 &&
              !this.props.isPicker
            }
            onLongPressStart={this.handleLongPressStart}
            onLongPressEnd={this.handleLongPressEnd}
            onLongPressCancel={this.handleLongPressCancel}
            onLongPressProgress={this.handleLongPressProgress}
          >
            <div
              className="PhoenixChart__PinchZoomPanTarget"
              style={{
                transform: `translate3d(${this.state.x}px, ${
                  this.state.y
                }px, 0) scale(${this.state.scale})`,
              }}
            >
              <div
                className="PhoenixChart__ChartContainer"
                ref={ref => (this.container = ref)}
              />
              {this.state.isConsideringFocus &&
              this.state.focusingProgress > 0.1 ? (
                <div
                  className="PhoenixChart__ProgressIndicator"
                  style={{
                    top: this.state.focusingY - FOCUS_SIZE / 2,
                    left: this.state.focusingX - FOCUS_SIZE / 2,
                    opacity: easeOutQuad(
                      between(
                        0,
                        1,
                        this.state.focusingProgress /
                          FOCUS_OPACITY_ANIMATION_THRESHOLD
                      )
                    ),
                  }}
                >
                  <div className="PhoenixChart__Donut">
                    <Donut
                      width={FOCUS_SIZE}
                      height={FOCUS_SIZE}
                      size={FOCUS_LINE_SIZE}
                      percentage={1}
                      color={COLOR__DOMOBLUE_A_40}
                    />
                  </div>
                  <div className="PhoenixChart__Donut">
                    <Donut
                      width={FOCUS_SIZE}
                      height={FOCUS_SIZE}
                      size={FOCUS_LINE_SIZE}
                      percentage={between(
                        0,
                        1,
                        easeOutQuad(
                          (this.state.focusingProgress -
                            FOCUS_ANIMATION_DEFER) /
                            (1 - FOCUS_ANIMATION_DEFER)
                        )
                      )}
                    />
                  </div>
                </div>
              ) : null}
            </div>
          </LongPressGesture>
          <PdpNotice chartRender={response} />
        </div>
      </Busy>
    );
  }
}

PhoenixChart.propTypes = {
  urn: PropTypes.string.isRequired,
  filterGroupIds: PropTypes.array,
  filters: PropTypes.arrayOf(
    PropTypes.shape({
      column: PropTypes.string.isRequired,
      dataSourceId: PropTypes.string,
      operand: PropTypes.string.isRequired,
      values: PropTypes.array.isRequired,
    })
  ),
  functionOverrides: PropTypes.object,
  dateTimeElement: PropTypes.string,
  dateRangeFilter: PropTypes.object,
  width: PropTypes.number.isRequired,
  height: PropTypes.number.isRequired,
  onDrill: PropTypes.func.isRequired,
  onLoad: PropTypes.func.isRequired,
  onRequestChartPickerClose: PropTypes.func.isRequired,
};

PhoenixChart.defaultProps = {
  onDrill: () => {},
  onLoad: () => {},
  onRequestChartPickerClose: () => {},
};

export default connect(
  (state, {urn}) => ({
    isEmbed: selectIsEmbed(state),
    isPublic: selectIsPublic(state),
    isExternal: selectIsExternal(state),
    isMap: selectIsMap(state, urn),
    chartState: selectChartStateByChartURN(state, urn),
    card: selectCardById(state, urn),
    isSummaryEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.SUMMARY),
    isDrillEnabled: selectIsCardSettingEnabled(state, CARD_SETTINGS.DRILL),
    isAddFilterTypeEnabled: selectIsFeatureEnabled(
      state,
      FEATURE_SWITCHES.ADD_FILTER_TYPE
    ),
  }),
  {
    updateChartState,
  },
  (stateProps, dispatchProps, ownProps) => ({
    ...ownProps,
    ...stateProps,
    ...dispatchProps,
    chartHeight:
      ownProps.height -
      (stateProps.isSummaryEnabled ? SUMMARY_NUMBER_HEIGHT : 0),
  })
)(LazilyLoadFactory(PhoenixChart, {phoenix: importPhoenix}));
